import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import _ from 'lodash';
import {
  fetchProfile,
  fetchOrders,
  sendReceipt,
  updateProfile,
  claimAccessCode,
  changePassword,
  logout,
} from '../utils/paymentApi';
import {
  getAuthToken,
  clearAuthToken,
  getGuestAccessCode,
} from '../utils/guestStorage';
import { showError, showSuccess } from '../utils/snackbar';
import ProfilePackages from '../components/profile/ProfilePackages';
import './Auth.css';
import './Profile.css';

// Normalize birth date to YYYY-MM-DD for MySQL DATE columns
const toDateOnly = (value) => {
  if (!value) {
    return '';
  }
  const raw = String(value);
  if (raw.length >= 10 && raw.charAt(4) === '-' && raw.charAt(7) === '-') {
    return raw.slice(0, 10);
  }
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) {
      return '';
    }
    return d.toISOString().slice(0, 10);
  } catch (e) {
    return '';
  }
};

// Profile dashboard: Profile | Order history | Packages
class Profile extends Component {
  constructor(props) {
    super(props);
    this.state = {
      tab: 'profile',
      user: null,
      guestId: null,
      guestAccessCode: getGuestAccessCode(),
      orders: [],
      loading: true,
      saving: false,
      claiming: false,
      claimCode: '',
      firstName: '',
      lastName: '',
      birthDate: '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      passwordSaving: false,
      receiptSendingId: '',
      initialLoadDone: false,
    };
  }

  componentDidMount() {
    this.load();
  }

  // True when right panel should show blocking circular loader
  isPanelBusy = () => {
    const { loading, saving, claiming, passwordSaving } = this.state;
    return !!(loading || saving || claiming || passwordSaving);
  };

  // Load profile + orders
  load = async () => {
    const token = getAuthToken();
    if (!token) {
      window.location.href = '/login';
      return;
    }
    try {
      this.setState({ loading: true });
      const me = await fetchProfile(token);
      const ordersRes = await fetchOrders(token);
      const user = _.get(me, 'user', null);
      this.setState({
        user,
        guestId: _.get(me, 'guestId', null),
        guestAccessCode: _.get(me, 'guestAccessCode', getGuestAccessCode()),
        orders: _.get(ordersRes, 'orders', []),
        firstName: _.get(user, 'firstName', ''),
        lastName: _.get(user, 'lastName', ''),
        birthDate: toDateOnly(_.get(user, 'birthDate', '') || ''),
        loading: false,
        initialLoadDone: true,
      });
    } catch (e) {
      clearAuthToken();
      window.location.href = '/login';
    }
  };

  setTab = (tab) => () => {
    if (this.isPanelBusy()) {
      return;
    }
    this.setState({ tab });
  };

  // Call BE logout then clear local Bearer token
  handleLogout = async () => {
    try {
      await logout();
    } catch (e) {
      // Ignore API errors — still clear local session
    }
    clearAuthToken();
    window.location.href = '/';
  };

  handleSaveProfile = async (event) => {
    event.preventDefault();
    if (this.isPanelBusy()) {
      return;
    }
    const token = getAuthToken();
    const birthDate = toDateOnly(this.state.birthDate) || undefined;
    try {
      this.setState({ saving: true });
      const result = await updateProfile(token, {
        firstName: this.state.firstName,
        lastName: this.state.lastName,
        birthDate,
      });
      this.setState({
        user: _.get(result, 'user', this.state.user),
        birthDate: toDateOnly(_.get(result, 'user.birthDate', birthDate) || birthDate || ''),
        saving: false,
      });
      showSuccess('Profile updated');
    } catch (e) {
      this.setState({ saving: false });
      showError(_.get(e, 'message', 'Update failed'));
    }
  };

  handleClaim = async (event) => {
    event.preventDefault();
    if (this.isPanelBusy()) {
      return;
    }
    const token = getAuthToken();
    try {
      this.setState({ claiming: true });
      await claimAccessCode(token, this.state.claimCode.trim());
      this.setState({ claimCode: '', claiming: false });
      showSuccess('Access code linked');
      this.load();
    } catch (e) {
      this.setState({ claiming: false });
      showError(_.get(e, 'message', 'Could not link code'));
    }
  };

  // Change password for email accounts only
  handleChangePassword = async (event) => {
    event.preventDefault();
    if (this.isPanelBusy()) {
      return;
    }
    const { currentPassword, newPassword, confirmPassword } = this.state;
    if (!newPassword || newPassword.length < 8) {
      showError('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      showError('New password and confirmation do not match');
      return;
    }
    const token = getAuthToken();
    try {
      this.setState({ passwordSaving: true });
      await changePassword(token, { currentPassword, newPassword });
      this.setState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        passwordSaving: false,
      });
      showSuccess('Password updated');
    } catch (e) {
      this.setState({ passwordSaving: false });
      showError(_.get(e, 'message', 'Could not change password'));
    }
  };

  handleSendReceipt = (orderId) => async () => {
    if (this.state.receiptSendingId) {
      return;
    }
    const token = getAuthToken();
    try {
      this.setState({ receiptSendingId: orderId });
      await sendReceipt(token, orderId);
      this.setState({ receiptSendingId: '' });
      showSuccess(`Receipt emailed for ${orderId}`);
    } catch (e) {
      this.setState({ receiptSendingId: '' });
      showError(_.get(e, 'message', 'Could not send receipt'));
    }
  };

  renderProfileTab() {
    const {
      user,
      firstName,
      lastName,
      birthDate,
      claimCode,
      currentPassword,
      newPassword,
      confirmPassword,
      passwordSaving,
      saving,
      claiming,
    } = this.state;
    const busy = this.isPanelBusy();
    if (!user) {
      return (
        <div>
          <p className="auth-sub">
            You are signed in with a Guest Access Code. Create an email account to receive
            on-demand receipts.
          </p>
          <Link className="auth-cta linkish" to="/signup">
            Create account
          </Link>
        </div>
      );
    }
    return (
      <div>
        <form onSubmit={this.handleSaveProfile}>
          <label htmlFor="pf-email">Email</label>
          <input id="pf-email" value={user.email || ''} readOnly />
          <label htmlFor="pf-first">First name</label>
          <input
            id="pf-first"
            value={firstName}
            disabled={busy}
            onChange={(e) => this.setState({ firstName: e.target.value })}
          />
          <label htmlFor="pf-last">Last name</label>
          <input
            id="pf-last"
            value={lastName}
            disabled={busy}
            onChange={(e) => this.setState({ lastName: e.target.value })}
          />
          <label htmlFor="pf-birth">Birth date</label>
          <input
            id="pf-birth"
            type="date"
            value={birthDate ? String(birthDate).slice(0, 10) : ''}
            disabled={busy}
            onChange={(e) => this.setState({ birthDate: e.target.value })}
          />
          <button type="submit" className="auth-cta" disabled={busy}>
            {saving ? 'Saving…' : 'Save profile'}
          </button>
        </form>
        <form className="profile-password" onSubmit={this.handleChangePassword}>
          <h3>Change password</h3>
          <label htmlFor="pf-cur-pass">Current password</label>
          <input
            id="pf-cur-pass"
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            disabled={busy}
            onChange={(e) => this.setState({ currentPassword: e.target.value })}
          />
          <label htmlFor="pf-new-pass">New password</label>
          <input
            id="pf-new-pass"
            type="password"
            autoComplete="new-password"
            value={newPassword}
            disabled={busy}
            onChange={(e) => this.setState({ newPassword: e.target.value })}
          />
          <label htmlFor="pf-confirm-pass">Confirm new password</label>
          <input
            id="pf-confirm-pass"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            disabled={busy}
            onChange={(e) => this.setState({ confirmPassword: e.target.value })}
          />
          <button type="submit" className="auth-cta secondary" disabled={busy}>
            {passwordSaving ? 'Saving…' : 'Update password'}
          </button>
        </form>
        <form className="profile-claim" onSubmit={this.handleClaim}>
          <h3>Link Guest Access Code</h3>
          <input
            value={claimCode}
            disabled={busy}
            onChange={(e) => this.setState({ claimCode: e.target.value })}
            placeholder="Guest Access Code"
          />
          <button type="submit" className="auth-cta secondary" disabled={busy}>
            {claiming ? 'Linking…' : 'Link orders'}
          </button>
        </form>
      </div>
    );
  }

  renderOrdersTab() {
    const orders = this.state.orders || [];
    const receiptSendingId = this.state.receiptSendingId || '';
    if (!orders.length) {
      return <p className="auth-sub">No orders yet.</p>;
    }
    return (
      <div className="profile-orders">
        {orders.map((order) => {
          const orderId = _.get(order, 'orderId', '');
          const status = _.get(order, 'status', '');
          const sending = receiptSendingId === orderId;
          const amount = ((Number(order.amountCents) || 0) / 100).toFixed(2);
          const currency = _.get(order, 'currency', 'USD');
          const statusClass =
            status === 'paid'
              ? 'profile-order-status profile-order-status-paid'
              : 'profile-order-status';
          return (
            <article className="profile-order-card" key={orderId}>
              <div className="profile-order-top">
                <div className="profile-order-heading">
                  <strong className="profile-order-title">
                    {order.product || 'LED Scroller Video Export'}
                  </strong>
                  {status ? <span className={statusClass}>{status}</span> : null}
                </div>
                <p className="profile-order-price">
                  ${amount} {currency}
                </p>
              </div>
              <div className="profile-order-id">Order ID: {orderId}</div>
              <div className="profile-order-stats">
                <span className="profile-order-stat">
                  Downloads: {order.downloadCount || 0}
                </span>
                {order.paidAt ? (
                  <span className="profile-order-stat">
                    Paid {new Date(order.paidAt).toLocaleString()}
                  </span>
                ) : null}
              </div>
              {status === 'paid' && this.state.user ? (
                <div className="profile-order-actions">
                  <button
                    type="button"
                    className="paywall-copy profile-receipt-btn"
                    onClick={this.handleSendReceipt(orderId)}
                    disabled={!!receiptSendingId}
                  >
                    {sending ? (
                      <span className="profile-spinner profile-spinner-sm" aria-hidden="true" />
                    ) : null}
                    {sending ? 'Sending…' : 'Send email receipt'}
                  </button>
                </div>
              ) : null}
            </article>
          );
        })}
      </div>
    );
  }

  render() {
    const { tab, user, initialLoadDone } = this.state;
    const panelBusy = this.isPanelBusy();
    const accountLabel = _.get(user, 'email', '') || 'Guest account';

    return (
      <div className="auth-page profile-page">
        <div className="profile-layout">
          <aside className="profile-sidebar">
            <nav className="profile-tabs" aria-label="Profile sections">
              <button
                type="button"
                className={tab === 'profile' ? 'active' : ''}
                onClick={this.setTab('profile')}
              >
                Profile
              </button>
              <button
                type="button"
                className={tab === 'orders' ? 'active' : ''}
                onClick={this.setTab('orders')}
              >
                Order history
              </button>
              <button
                type="button"
                className={tab === 'packages' ? 'active' : ''}
                onClick={this.setTab('packages')}
              >
                Packages
              </button>
            </nav>
            <div className="profile-account">
              <span className="profile-account-email" title={accountLabel}>
                {accountLabel}
              </span>
              <button
                type="button"
                className="profile-logout"
                onClick={this.handleLogout}
              >
                Log out
              </button>
            </div>
          </aside>

          <section className="auth-card profile-main">
            {panelBusy ? (
              <div className="profile-panel-loader" role="status" aria-live="polite">
                <span className="profile-spinner" aria-hidden="true" />
                <span className="profile-panel-loader-text">
                  {this.state.loading ? 'Loading…' : 'Saving…'}
                </span>
              </div>
            ) : null}
            <div className={`profile-main-body${panelBusy ? ' profile-main-body-busy' : ''}`}>
              <h1 className="auth-title">
                {tab === 'profile' && 'Profile'}
                {tab === 'orders' && 'Order history'}
                {tab === 'packages' && 'Packages'}
              </h1>
              {!initialLoadDone && this.state.loading ? (
                <p className="auth-sub">Loading your account…</p>
              ) : null}
              {initialLoadDone && tab === 'profile' ? this.renderProfileTab() : null}
              {initialLoadDone && tab === 'orders' ? this.renderOrdersTab() : null}
              {initialLoadDone && tab === 'packages' ? <ProfilePackages /> : null}
            </div>
          </section>
        </div>
      </div>
    );
  }
}

export default Profile;
